/**
 * Beu admin — AI overview.
 *
 * Per-salon AI + messaging revenue, our provider cost, and the margin, for a
 * month. Every number drills down: salon → sessions/calls → every line.
 * Backed by /api/v2/admin/ai (cost ledger, aiusageevents).
 */
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AdminWrapper from '../AdminWrapper';
import { adminAiService } from '../../../_services/adminAi.service';
import {
  PRODUCT_LABELS, FLAG_LABELS, fmtPence, fmtPct, fmtInt, recentMonths, monthLabel, sortSalons, filterSalons
} from './aiFormat';
import UnitEconomics from './UnitEconomics';
import '../Admin.css';
import '../financials/AdminFinancials.css';
import './AdminAi.css';

export function FlagPills({ flags }) {
  return (
    <span className="ai-ov-flags">
      {(flags || []).map((f) => {
        const meta = FLAG_LABELS[f] || { label: f, tone: 'dim' };
        return <span key={f} className={`ai-ov-flag ${meta.tone}`}>{meta.label}</span>;
      })}
    </span>
  );
}

export function MarginCell({ margin, pct }) {
  return (
    <span className={margin < 0 ? 'ai-ov-neg' : 'ai-ov-pos'}>
      {fmtPence(margin)}{pct != null && <span className="ai-ov-pct"> ({fmtPct(pct)})</span>}
    </span>
  );
}

export function MonthPicker({ value, onChange }) {
  return (
    <select className="ai-ov-month" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Month">
      {recentMonths().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
    </select>
  );
}

const COLS = [
  { key: 'salonName', label: 'Salon' },
  { key: 'revenue', label: 'Revenue', num: true },
  { key: 'cost', label: 'Cost', num: true },
  { key: 'margin', label: 'Margin', num: true },
  { key: 'marginPct', label: 'Margin %', num: true },
  { key: 'events', label: 'Events', num: true }
];

class AdminAiOverview extends Component {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(props.location?.search || '');
    this.state = {
      month: params.get('month') || recentMonths()[0],
      loading: true, error: null, data: null,
      sortKey: 'cost', sortDir: 'desc', search: '', flag: '', exporting: false
    };
  }

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, error: null });
    try {
      const data = await adminAiService.getOverview(this.state.month);
      this.setState({ data, loading: false });
    } catch (e) {
      this.setState({ error: e.message || String(e), loading: false });
    }
  };

  setMonth = (month) => {
    this.setState({ month }, this.load);
    this.props.history && this.props.history.replace(`/beu-admin-x7k9/ai?month=${month}`);
  };

  sortBy = (key) => {
    this.setState((s) => ({ sortKey: key, sortDir: s.sortKey === key && s.sortDir === 'desc' ? 'asc' : 'desc' }));
  };

  exportCsv = async (which) => {
    this.setState({ exporting: true });
    try {
      if (which === 'events') await adminAiService.exportEventsCsv(this.state.month);
      else await adminAiService.exportOverviewCsv(this.state.month);
    } catch (e) { window.alert(e.message || 'Export failed'); }
    this.setState({ exporting: false });
  };

  renderHeadline(d) {
    const t = d.totals;
    const tiles = [
      { label: 'AI + messaging revenue', value: fmtPence(t.revenue) },
      { label: 'Provider cost', value: fmtPence(t.cost), sub: `${fmtInt(t.events)} billable events · ${fmtInt(t.reconciledEvents)} reconciled` },
      { label: 'Margin', value: <MarginCell margin={t.margin} />, sub: `Margin ${fmtPct(t.marginPct)}` },
      { label: 'Bailey bundle margin', value: <MarginCell margin={d.baileyBundle.margin} pct={d.baileyBundle.marginPct} />, sub: 'Upgrade + packs vs assistant + receptionist' }
    ];
    return (
      <div className="fin-kpi-strip">
        {tiles.map((x) => (
          <div key={x.label} className="fin-kpi-tile">
            <div className="fin-kpi-tile-label">{x.label}</div>
            <div className="fin-kpi-tile-value">{x.value}</div>
            {x.sub && <div className="fin-kpi-tile-sub">{x.sub}</div>}
          </div>
        ))}
      </div>
    );
  }

  renderByProduct(d) {
    const a = d.allocation || {};
    const pct = (o) => Object.entries(o || {}).map(([k, v]) => `${PRODUCT_LABELS[k] || k} ${Math.round(v * 100)}%`).join(', ');
    return (
      <div className="fin-section">
        <div className="fin-section-header">
          <h3 className="fin-section-title">By product</h3>
          <span className="fin-section-sub">Rate card {d.rateCard?.version}</span>
        </div>
        <table className="fin-table">
          <thead><tr><th>Product</th><th className="num">Revenue</th><th className="num">Cost</th><th className="num">Margin</th><th className="num">Margin %</th></tr></thead>
          <tbody>
            {d.byProduct.map((p) => (
              <tr key={p.product}>
                <td>{PRODUCT_LABELS[p.product] || p.product}</td>
                <td className="num">{fmtPence(p.revenue)}</td>
                <td className="num">{fmtPence(p.cost)}</td>
                <td className="num"><MarginCell margin={p.margin} /></td>
                <td className="num">{fmtPct(p.marginPct)}</td>
              </tr>
            ))}
            <tr className="total">
              <td>Total</td>
              <td className="num">{fmtPence(d.totals.revenue)}</td>
              <td className="num">{fmtPence(d.totals.cost)}</td>
              <td className="num"><MarginCell margin={d.totals.margin} /></td>
              <td className="num">{fmtPct(d.totals.marginPct)}</td>
            </tr>
          </tbody>
        </table>
        <p className="fin-dim ai-ov-note">
          Bailey Plus counts as the assistant; the AI receptionist, its pay as you go and packs are split {pct(a.pack)} (an old Beu + Bailey upgrade: {pct(a.upgrade)}). Both add-ons on one location count at the bundle price. Plan base subscriptions are not counted, so reminder and booking texts show their full cost.
          {d.partialMonth ? ' This month is to date.' : ''}
        </p>
        {d.rateCard?.unverified?.length > 0 && (
          <details className="ai-ov-unverified">
            <summary>{d.rateCard.unverified.length} rates on the card are not yet verified</summary>
            <ul>{d.rateCard.unverified.map((u) => <li key={u.rate}><code>{u.rate}</code>{u.note ? ` — ${u.note}` : ''}</li>)}</ul>
          </details>
        )}
      </div>
    );
  }

  renderSalons(d) {
    const { sortKey, sortDir, search, flag, month } = this.state;
    const rows = sortSalons(filterSalons(d.salons, { search, flag }), sortKey, sortDir);
    return (
      <div className="fin-section">
        <div className="fin-section-header">
          <h3 className="fin-section-title">Salons ({rows.length})</h3>
          <div className="ai-ov-filters">
            <input className="ai-ov-search" placeholder="Search salon" value={search} onChange={(e) => this.setState({ search: e.target.value })} />
            <select className="ai-ov-month" value={flag} onChange={(e) => this.setState({ flag: e.target.value })} aria-label="Flag">
              <option value="">All salons</option>
              {Object.entries(FLAG_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div className="ai-ov-scroll">
          <table className="fin-table ai-ov-table">
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.key} className={`${c.num ? 'num' : ''} ai-ov-sortable`} onClick={() => this.sortBy(c.key)}>
                    {c.label}{sortKey === c.key ? (sortDir === 'desc' ? ' ▾' : ' ▴') : ''}
                  </th>
                ))}
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.salonId || 'unattributed'}>
                  <td>
                    {r.salonId
                      ? <Link to={`/beu-admin-x7k9/ai/salon/${r.salonId}?month=${month}`}>{r.salonName}</Link>
                      : <span className="fin-dim">{r.salonName}</span>}
                  </td>
                  <td className="num">{fmtPence(r.revenue)}</td>
                  <td className="num">{fmtPence(r.cost)}</td>
                  <td className="num"><MarginCell margin={r.margin} /></td>
                  <td className="num">{fmtPct(r.marginPct)}</td>
                  <td className="num">{fmtInt(r.events)}</td>
                  <td><FlagPills flags={r.flags} /></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="fin-dim">No AI or messaging activity this month.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const { month, loading, error, data, exporting } = this.state;
    return (
      <AdminWrapper>
        <div className="admin-page-header ai-ov-header">
          <div>
            <h1 className="admin-page-title">AI overview</h1>
            <p className="admin-page-subtitle">What each salon pays for AI and messaging, what it costs us, and the margin</p>
          </div>
          <div className="ai-ov-actions">
            <MonthPicker value={month} onChange={this.setMonth} />
            <button className="ai-ov-btn" disabled={exporting} onClick={() => this.exportCsv('overview')}>Export salons CSV</button>
            <button className="ai-ov-btn" disabled={exporting} onClick={() => this.exportCsv('events')}>Export all events CSV</button>
          </div>
        </div>
        {loading && <div className="fin-loading">Loading AI overview…</div>}
        {error && <div className="fin-error">{error}</div>}
        {!loading && !error && data && (
          <div>
            {this.renderHeadline(data)}
            {this.renderByProduct(data)}
            <UnitEconomics data={data.unitEconomics} />
            {this.renderSalons(data)}
          </div>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminAiOverview;
